/**
 * Mock mode for the Fias Forge test window (ADR-023 A16).
 *
 * The test window runs a forge's test build through the production render
 * path, so every call that costs credits — AI text, the billed integrations,
 * image and audio generation, invocation surfaces — is real and charged to
 * the owner. In Mock mode the HOST answers those calls in the owner's browser
 * instead: from the sample answers Claude wrote for the build
 * (`fias-dev.config.json` `mockEntities`, shipped with each test build), or a
 * placeholder. They never reach the server, so nothing is charged — and
 * nothing here can make a real call free, because a Mock call is not made at
 * all. Free calls (the test tenant's storage, data and workspaces) are
 * untouched in either mode.
 */
export type TestChannelPolicy = 'isolated' | 'real' | 'simulated' | 'unavailable';

/**
 * Client mirror of `pluginInvocable.testChannelPolicy` for every
 * server-dispatched integration entity (`@fias/entities`; the client cannot
 * import it). `real` means the test channel serves — and bills — the real
 * thing; Mock answers exactly those. A target absent from this map is an AI
 * model or a capability selector. Pinned to the catalog in both directions by
 * `tests/architecture/client-test-channel-policy-mirror.test.ts`.
 */
export const INTEGRATION_ENTITY_TEST_CHANNEL_POLICIES: Readonly<Record<string, TestChannelPolicy>> =
  {
    ent_intg_community_assets_000000: 'unavailable',
    ent_intg_company_email_send_0000: 'unavailable',
    ent_intg_dfe_game_rewards_000000: 'unavailable',
    ent_intg_dfe_game_state_00000000: 'unavailable',
    ent_intg_document_sign_000000000: 'unavailable',
    ent_intg_elevenlabs_align_000000: 'real',
    ent_intg_elevenlabs_isolate_0000: 'real',
    ent_intg_elevenlabs_music_000000: 'real',
    ent_intg_elevenlabs_sfx_00000000: 'real',
    ent_intg_elevenlabs_stt_00000000: 'real',
    ent_intg_elevenlabs_tts_00000000: 'real',
    ent_intg_elevenlabs_vc_000000000: 'real',
    ent_intg_elevenlabs_vdesign_0000: 'real',
    ent_intg_gemini_transcribe_00000: 'real',
    ent_intg_gemini_tts_flash_000000: 'real',
    ent_intg_gemini_tts_flash_lite00: 'real',
    ent_intg_litcraft_player_0000000: 'unavailable',
    ent_intg_mining_0000000000000000: 'unavailable',
    ent_intg_plugin_datastore_000000: 'isolated',
    ent_intg_plugin_storage_00000000: 'isolated',
    ent_intg_plugin_store_0000000000: 'simulated',
    ent_intg_plugin_workspaces_00000: 'isolated',
    ent_intg_vault_arche_docs_000000: 'unavailable',
    ent_intg_vault_documents_0000000: 'unavailable',
    ent_intg_web_search0000000000000: 'real',
  };

/** One sample answer, as Claude writes it in `fias-dev.config.json`. */
export interface TestChannelMockSample {
  response: string;
}

/** Samples keyed like the dev harness: an entity id, or `capability:<name>`. */
export type TestChannelMockSamples = Readonly<Record<string, TestChannelMockSample>>;

/** The error code of a call Mock cannot answer. */
export const MOCK_UNAVAILABLE_CODE = 'MOCK_UNAVAILABLE';

/** Why Mock cannot answer a call, and how the owner can try it for real. */
export function mockUnavailableMessage(what: string): string {
  return `${what} has no sample answer in Mock mode. Switch the test window to Live to try it (it uses your credits).`;
}

export type MockEntityInvokeOutcome =
  | { kind: 'answer'; result: { output: unknown; metadata: Record<string, unknown> } }
  | { kind: 'unavailable'; message: string };

/** What a Mock answer reports as its usage: free. */
const MOCK_USAGE = { inputTokens: 0, outputTokens: 0, costCredits: 0 };

/**
 * The key a sample is filed under — the dev harness's rule, so a sample
 * Claude tested with in the forge answers the same call here.
 */
export function mockSampleKey(entityId: unknown): string | null {
  if (typeof entityId === 'string') return entityId;
  if (entityId && typeof entityId === 'object') {
    const capability = (entityId as { capability?: unknown }).capability;
    if (typeof capability === 'string') return `capability:${capability}`;
  }
  return null;
}

/** A billed integration's sample is usually JSON; anything else is the output as written. */
function parseSample(response: string): unknown {
  try {
    return JSON.parse(response);
  } catch {
    return response;
  }
}

/**
 * Answer an `entity_invoke` in Mock mode, or return null when the call is
 * free and should go to the server as usual (storage, data, workspaces, the
 * simulated store, and anything the test channel refuses anyway).
 *
 * - AI text (a model id or a capability selector): the sample, else a
 *   placeholder naming what has no sample.
 * - A billed integration (policy `real`): the sample, else `unavailable` — a
 *   placeholder string in place of a structured result would only fail
 *   somewhere less obvious.
 */
export function mockEntityInvoke(
  entityId: unknown,
  samples: TestChannelMockSamples,
): MockEntityInvokeOutcome | null {
  const key = mockSampleKey(entityId);
  const policy =
    typeof entityId === 'string' &&
    Object.prototype.hasOwnProperty.call(INTEGRATION_ENTITY_TEST_CHANNEL_POLICIES, entityId)
      ? INTEGRATION_ENTITY_TEST_CHANNEL_POLICIES[entityId]
      : undefined;
  if (policy !== undefined && policy !== 'real') return null;
  const sample =
    key !== null && Object.prototype.hasOwnProperty.call(samples, key) ? samples[key] : undefined;
  const metadata = { entityId: key ?? 'unknown', mock: true, usage: MOCK_USAGE };
  if (policy === 'real') {
    if (!sample)
      return { kind: 'unavailable', message: mockUnavailableMessage(`This call (${key})`) };
    return { kind: 'answer', result: { output: parseSample(sample.response), metadata } };
  }
  return {
    kind: 'answer',
    result: {
      output: sample
        ? sample.response
        : `[Mock] No sample answer for ${key ?? 'this model'} in this test build.`,
      metadata,
    },
  };
}

/** A neutral "mock image" picture, so a layout that shows a generated image still has one. */
export const MOCK_IMAGE_DATA_URL = `data:image/svg+xml;utf8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="0 0 512 512">' +
    '<rect width="512" height="512" fill="#e5e7eb"/>' +
    '<path d="M96 384l96-128 72 96 48-64 104 96z" fill="#9ca3af"/>' +
    '<circle cx="352" cy="176" r="40" fill="#9ca3af"/>' +
    '<text x="256" y="464" font-family="sans-serif" font-size="28" text-anchor="middle" fill="#4b5563">Mock image</text>' +
    '</svg>',
)}`;

/** Answer `image_generate` in Mock mode: the placeholder picture, free. */
export function mockImageResult(): Record<string, unknown> {
  return {
    imageUrl: MOCK_IMAGE_DATA_URL,
    imageRef: 'mock://image',
    provider: 'mock',
    model: 'mock',
    costCredits: 0,
  };
}

/** A silent mono 16-bit PCM WAV of the given length, as a data URL. */
export function silentWavDataUrl(
  seconds: number,
  sampleRate = 8000,
): { url: string; bytes: number } {
  const samples = Math.max(1, Math.round(seconds * sampleRate));
  const dataBytes = samples * 2;
  const buffer = new ArrayBuffer(44 + dataBytes);
  const view = new DataView(buffer);
  const ascii = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i));
  };
  ascii(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  ascii(36, 'data');
  view.setUint32(40, dataBytes, true);
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return { url: `data:audio/wav;base64,${btoa(binary)}`, bytes: bytes.length };
}

/** Answer `audio_generate` in Mock mode: one second of silence, free. */
export function mockAudioResult(audioType: unknown): Record<string, unknown> {
  const { url, bytes } = silentWavDataUrl(1);
  return {
    audioType: typeof audioType === 'string' ? audioType : 'speech',
    clips: [
      {
        audioUrl: url,
        audioRef: 'mock://audio',
        mimeType: 'audio/wav',
        sampleRate: 8000,
        durationSeconds: 1,
        sizeBytes: bytes,
      },
    ],
    totalDurationSeconds: 1,
    provider: 'mock',
    model: 'mock',
  };
}
